import { memo } from 'react';
import { Alert, Button, SpaceBetween } from '@amzn/awsui-components-core';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

import { useAddSectionButtonViewModel } from './AddSectionButtonViewModel';

interface AddSectionButtonProps {
  readonly currentPage: ContentNode;
  /** Called with the new section's id after it is staged, so the caller can flag it for autofocus. */
  readonly onSectionAdded?: (newSectionId: string) => void;
}

/**
 * Plus-icon button that adds a new section to an existing page of a guide.
 * Clicking it appends an "Untitled" section to the current page and persists
 * the change; the new section's id is reported via {@link AddSectionButtonProps.onSectionAdded}
 * so the caller can autofocus the new section's editor. Shows a loading
 * spinner while saving and an inline alert if the save fails.
 */
const AddSectionButton = memo(function AddSectionButton({
  currentPage,
  onSectionAdded,
}: AddSectionButtonProps) {
  const vm = useAddSectionButtonViewModel(currentPage, onSectionAdded);

  return (
    <SpaceBetween size="xs">
      <Button iconName="add-plus" onClick={vm.addSection} loading={vm.isAdding}>
        Add section
      </Button>
      {vm.addErrorMessage !== undefined && <Alert type="error">{vm.addErrorMessage}</Alert>}
    </SpaceBetween>
  );
});

export default AddSectionButton;
